import React from 'react';
import { withRouter } from 'react-router-dom';
import moment from 'moment';
import Days from './Days';
import Times from './Times';

import { useHttp } from '@invitation-homes/styled-ui/lib/hooks/use-http';
import CircleLoader from '@invitation-homes/styled-ui/lib/components/Loader/CircleLoader';
import FormSection from '@invitation-homes/styled-ui/lib/components/Form/Section';
import Grid from '@material-ui/core/Grid';

import Box from '@material-ui/core/Box';

import { PageSubHeading, Heading, PageHeading } from '../components/CardCommon';
import ErrorMessage from '../components/ErrorMessage';
import ShowingNav from '../components/ShowingNav';
import SchedulingShowingStatus from '../components/SchedulingShowingStatus';

const DEFAULT_TRUSTED_VALIDITY_DAYS = 30;

const isTrustedUser = (user) => {
  const trustedValidityDays = process.env.REACT_APP_TRUSTED_VALIDITY_DAYS || DEFAULT_TRUSTED_VALIDITY_DAYS;
  return !!user && user.trusted && moment(user.trusted_at).add(trustedValidityDays, 'days') > moment();
};

/**
 * Keep Control's legacy authentication route while allowing Treatment to own
 * continuation directly.
 * @param {object} params
 * @returns {{ handleNextClick: Function | false | undefined, nextTo: object | undefined }}
 */
function getShowingNavProps({ onContinue, isTrusted, createShowing, match, location }) {
  if (onContinue) {
    return { handleNextClick: onContinue, nextTo: undefined };
  }

  if (isTrusted) {
    return { handleNextClick: createShowing, nextTo: undefined };
  }

  return {
    handleNextClick: false,
    nextTo: {
      pathname: `${match.url}/authentication`,
      search: location.search,
    },
  };
}

/**
 * Presentational day/time picker. Availability fetching stays in the wrapper
 * unless a parent already loaded preview data.
 */
export function DayTimePickerView({
  unitAddress,
  daySelected,
  timeSelected,
  location,
  match,
  onChange,
  createShowing,
  onContinue,
  user,
  submitting,
  data,
  error,
  loading,
}) {
  const isTrusted = isTrustedUser(user);
  const { handleNextClick, nextTo } = getShowingNavProps({
    onContinue,
    isTrusted,
    createShowing,
    match,
    location,
  });

  if (submitting) {
    return <SchedulingShowingStatus unitAddress={unitAddress} timeSelected={timeSelected} />;
  }

  if (loading) {
    return (
      <Grid container justify="center" spacing={0}>
        <Grid item xs={12} sm={10} md={8}>
          <Box py={20}>
            <CircleLoader size={20} />
          </Box>
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid container justify="center" spacing={0}>
      <Grid item xs={12}>
        {!error && unitAddress && (
          <PageHeading>
            Schedule Self-Tour <PageSubHeading>for {unitAddress}</PageSubHeading>
          </PageHeading>
        )}
        <FormSection>
          {error ? (
            <ErrorMessage type={data.error} unitId={match.params} />
          ) : (
            <div role="radiogroup" aria-labelledby="days_heading">
              <Heading id="days_heading">Select A Day</Heading>
              <Days
                initialNumShown={4}
                onSelect={(day) => {
                  onChange('daySelected', day);
                }}
                selected={daySelected}
                available={data.available}
                timezone={data.unitTimezone}
              />
            </div>
          )}
        </FormSection>
        {daySelected && (
          <FormSection>
            <Heading>Select A Time</Heading>
            <Times
              onSelect={(time) => {
                onChange('timeSelected', time);
              }}
              selectedTime={timeSelected}
              selectedDay={daySelected}
              available={data.available}
              timezone={data.unitTimezone}
            />
          </FormSection>
        )}
        {!error && (
          <ShowingNav
            next
            handleNextClick={handleNextClick}
            nextDisabled={!daySelected || !timeSelected || submitting}
            nextTo={nextTo}
            submitting={submitting}
          />
        )}
      </Grid>
    </Grid>
  );
}

// render the picker for the control variation
function ConnectedDayTimePicker(props) {
  const { unit_id } = props.match.params;
  const { data, error, loading } = useHttp({
    endpoint: `/self-tours/api/v2/availability?unit_id=${unit_id}`,
  });

  return <DayTimePickerView {...props} data={data} error={error} loading={loading} />;
}

function DayTimePicker(props) {
  if (props.availability) {
    // render the picker for the treatment variation
    return <DayTimePickerView {...props} data={props.availability} error={false} loading={false} />;
  }

  return <ConnectedDayTimePicker {...props} />;
}

export default withRouter(DayTimePicker);
